<template>
    <div>
        <div class="mt-6 pb-6" v-if="enrollment.course.applications.length === 0">
            <ui-card class="pt-0 pl-4 pr-6">
                <div class="columns is-gapless mb-6 pt-5">
                    <div class="column is-narrow"><i class="far fa-fw fa-info-circle mr-2"></i></div>
                    <div class="column">
                        <div class="has-text-weight-bold">This course does not have any applications or forms.</div>
                    </div>
                </div>
            </ui-card>
        </div>
        <ui-card v-else class="pt-4 pl-4 pr-6 pb-3 mb-5" v-for="app in enrollment.course.applications">
            <div class="columns is-gapless">
                <div class="column is-narrow is-size-6">
                    <i class="fad fa-fw fa-2x has-text-primary mr-5" :class="{'fa-file-certificate': app.applications.type === 'application', 'fa-file-invoice': app.applications.type === 'form'}"></i>
                </div>
                <div class="column mr-5">
                    <div class="has-text-weight-bold is-size-5 mt-1">
                        {{ app.applications.title }}
                        <b-tooltip label="Additional Fee Required" type="is-info" v-if="hasCharge(app)">
                            <b-tag type="is-warning is-light" class="ml-3 mr-3"><i class="fas fa-dollar"></i></b-tag>
                        </b-tooltip>
                        <!-- Required badge is a readiness-gated nag: only shown once the
                             student is genuinely done working (never earlier) -->
                        <b-tooltip label="Required Application" type="is-info" v-if="app.is_required_at_end && !getEnrollApp(app).completed_at && applicationReadiness">
                            <b-tag type="is-warning is-light" class="ml-3 mr-3"><i class="fas fa-exclamation-triangle mr-2"></i>Required</b-tag>
                        </b-tooltip>

                    </div>
                    <div class="is-size-6 has-text-grey" v-if="app.applications.type === 'application'">{{ app.applications.certification_certs.certifications.title }}</div>
                    <div class="is-size-6 has-text-grey mt-3" v-html="app.applications.description"></div>

                    <div class="mt-3" v-if="app.is_required_at_start && app.isMissingRequired">
                        <div><i class="fas fa-exclamation-triangle has-text-warning mr-2 is-size-6"></i>Completion of this {{ app.applications.type }} is required before starting the course.</div>
                    </div>

                    <div v-if="!app.is_enabled && !getEnrollApp(app).completed_at" class="mt-5">
                        <b-message type="is-warning">
                            <div>This application is not available at this time. Either it has not been made available yet, or it has expired. For the most accurate and up-to-date information, please contact the course's provider.</div>
                            <div class="mt-4 pr-6 has-text-weight-bold">
                                <i class="far fa-fw fa-arrow-right mr-2"></i>Please, check back later or contact the course's provider for more information.
                            </div>
                        </b-message>
                    </div>

                    <!-- Per-certification switch: an application for a certification the
                         user has turned off stays visible but its tools wait for the switch.
                         Applications already started or completed always show in full. -->
                    <section v-else-if="certificationOffFor(app) && !getEnrollApp(app).id" class="mt-4">
                        <div class="cert-off">
                            <div class="has-text-grey"><i class="far fa-toggle-off mr-2"></i>Available with <strong>{{ certificationOffFor(app).certification.title }}</strong>. Turn it on under Your Account to use this {{ app.applications.type }}.</div>
                            <b-button type="is-primary" size="is-small" outlined tag="router-link" :to="{name: 'account', params: {section: 'certs'}}" class="mt-3"><i class="far fa-toggle-on mr-2"></i>Turn On</b-button>
                        </div>
                    </section>

                    <section v-else>
                        <div v-if="!getEnrollApp(app).id">
                            <div v-if="app.applications.certification_certs && app.applications.certification_certs.certifications.type === 'unit'">
                                <div class="mt-5 pr-6 pb-2">You have recorded <strong>{{ calcUnits(app).toFixed(2) }}</strong> of <strong>{{ availableUnits(app).toFixed(2) }}</strong> available {{ app.applications.certification_certs.certifications.unit_title }}.</div>

                                <div v-if="calcUnits(app) > 0">
                                    <b-progress
                                        size="is-small"
                                        type="is-success"
                                        :value="calcUnits(app)"
                                        :max="availableUnits(app)"
                                        class="mb-4"
                                    ></b-progress>
                                    <div class="help"><i class="far fa-info-circle mr-1"></i> {{ app.applications.certification_certs.certifications.unit_title }} are calculated from your logged study time, up to the hours you enrolled in. Note that the final {{ app.applications.certification_certs.certifications.unit_title }} calculation may differ due to rounding.</div>
                                </div>
                                <div class="mt-3" v-else>
                                    <i class="far fa-fw fa-arrow-right mr-2"></i>Your log is empty. Add sessions from the <strong><i class="far fa-calendar-circle-user mr-2 ml-1"></i>Sessions & Schedules</strong> or watch <strong><i class="far fa-clapperboard-play mr-2 ml-1"></i>On-Demand Content</strong> to contribute to the log.
                                </div>
                            </div>

                            <hr/>

                            <div v-if="!app.ready.toggle">
                                <b-button type="is-info" v-if="app.applications.type === 'application'" @click="start(app)"><strong>View Details</strong><i class="far fa-arrow-right-long ml-3"></i></b-button>
                                <b-button type="is-info" v-if="app.applications.type === 'form'" @click="apply(app)"><strong>Start Form</strong><i class="far fa-arrow-right-long ml-3"></i></b-button>
                            </div>

                            <div v-show="app.ready.toggle">
                                <div class="mt-6">
                                    <b-message type="is-info">
                                        <p class="has-text-weight-bold is-size-5">Are you ready to apply?</p>
                                        <p class="mb-5">Please review and check the following to verify that you are ready to apply for this certification:</p>

                                        <div class="columns is-gapless">
                                            <div class="column is-narrow">
                                                <b-switch type="is-success" class="mr-4" v-model="app.ready.log"></b-switch>
                                            </div>
                                            <div class="column">
                                                I have double-checked my <strong><i class="far fa-clipboard-list-check mr-2 ml-1"></i>Study Log</strong> for accuracy and have verified that all entries are truthful and accurate, if applicable.
                                            </div>
                                        </div>
                                        <div class="columns is-gapless">
                                            <div class="column is-narrow">
                                                <b-switch type="is-success" class="mr-4" v-model="app.ready.confirm"></b-switch>
                                            </div>
                                            <div class="column">
                                                I understand that once I apply, <u>changes to my log will not make any changes to this application or this certification</u>. You cannot reapply for this certification once you have applied.
                                            </div>
                                        </div>
                                        <section v-if="hasCharge(app)">
                                            <hr style="background: rgb(158 198 231)"/>
                                            <div class="columns is-gapless">

                                                <div class="column is-narrow">
                                                    <b-switch type="is-success" class="mr-4" v-model="app.ready.charge"></b-switch>
                                                </div>
                                                <div class="column">
                                                    <p v-if="perUnitRate(app) && calcUnits(app) === 0">I understand this application is billed at <strong>{{ perUnitRate(app) | currency }}</strong> per {{ unitSingular(app) }} for the {{ app.applications.certification_certs.certifications.unit_title }} I claim.  This fee is separate from any registration fees, if applicable, and will be collected at the end of the application, if approved. Application fees are non-refundable.</p>
                                                    <p v-else-if="app.applications.certification_certs && app.applications.certification_certs.certifications.type === 'unit'">I understand the cost of this application is <strong>{{ chargeAmount(app) | currency }}</strong><template v-if="perUnitRate(app)"> (<strong>{{ perUnitRate(app) | currency }}</strong> per {{ unitSingular(app) }})</template> for <strong>{{ calcUnits(app).toFixed(2) }} {{ app.applications.certification_certs.certifications.unit_title }}</strong>.  This fee is separate from any registration fees, if applicable, and will be collected at the end of the application, if approved. Application fees are non-refundable.</p>
                                                    <p v-else>I understand the cost of this application is <strong>{{ chargeAmount(app) | currency }}</strong> for <strong>{{ app.applications.certification_certs.certifications.unit_title }}</strong>.  This fee is separate from enrollment fees, if applicable, and will be collected at the end of the application, if approved. Application fees are non-refundable.</p>
                                                </div>
                                            </div>
                                        </section>
                                    </b-message>

                                    <b-message class="mt-5">
                                        <section>
                                            <p class="has-text-weight-bold is-size-5 ">Other Requirements</p>

                                            <div class="mb-3">
                                                <div class="columns is-gapless" v-if="!enrollment.isCourseworkComplete">
                                                    <div class="column is-narrow"><i class="fal ml-1 mr-3 fa-ban fa-2x fa-fw has-text-warning"></i></div>
                                                    <div class="column">
                                                        <div class="mt-1">You must finish all lessons in this course to apply for this certification. Please review the course content in the <strong><i class="far fa-rectangle-list mr-2 ml-1"></i>Course Outline</strong>.</div>
                                                    </div>
                                                </div>
                                                <div class="columns is-gapless" v-else>
                                                    <div class="column is-narrow"><i class="fal ml-1 mr-3 fa-check-circle fa-2x fa-fw has-text-success"></i></div>
                                                    <div class="column">
                                                        <div class="mt-1">You must finish all lessons in this course to apply for this certification.</div>
                                                    </div>
                                                </div>
                                            </div>

                                            <div class="mb-3" v-if="milestoneLock.locked">
                                                <div class="columns is-gapless">
                                                    <div class="column is-narrow"><i class="fal ml-1 mr-3 fa-ban fa-2x fa-fw has-text-warning"></i></div>
                                                    <div class="column">
                                                        <div class="mt-1">A milestone reflection is awaiting approval. It must be approved before you can apply. Review your <strong><i class="far fa-flag-swallowtail mr-2 ml-1"></i>Milestones &amp; Reflections</strong>.</div>
                                                    </div>
                                                </div>
                                            </div>

                                            <div v-if="app.min_minutes_required" class="mb-3">
                                                <div class="columns is-gapless" v-if="totalLogMinutes < app.min_minutes_required">
                                                    <div class="column is-narrow"><i class="fal ml-1 mr-3 fa-ban fa-2x fa-fw has-text-warning"></i></div>
                                                    <div class="column">
                                                        <div class="mt-1">You must have recorded at least <strong>{{ app.min_minutes_required }}</strong> minute(s) to apply for this certification. Please review your <strong><i class="far fa-clipboard-list-check mr-2 ml-1"></i>Study Log</strong>.</div>
                                                        <div class="mt-1 is-size-7 has-text-grey">You currently have {{ totalLogMinutes }} minute(s) recorded.</div>
                                                    </div>
                                                </div>
                                                <div class="columns is-gapless" v-else>
                                                    <div class="column is-narrow"><i class="fal ml-1 mr-3 fa-check-circle fa-2x fa-fw has-text-success"></i></div>
                                                    <div class="column">
                                                        <div class="mt-1">You must have recorded at least <strong>{{ app.min_minutes_required }}</strong> minute(s) to apply for this certification.</div>
                                                    </div>
                                                </div>
                                            </div>
                                        </section>
                                    </b-message>
                                </div>

                                <div class="mt-6" v-if="app.applications.type === 'application'">
                                    <b-button
                                        size="is-large"
                                        type="is-info"
                                        expanded
                                        :disabled="!applyReady(app) || processing"
                                        @click="apply(app)"
                                        v-if="app.applications.certification_certs && app.applications.certification_certs.certifications.type === 'unit'"
                                    >
                                        <i class="fas fa-arrow-right mr-4"></i>Apply for <strong>{{ calcUnits(app).toFixed(2) }} {{ app.applications.certification_certs.certifications.unit_title }}</strong>
                                    </b-button>

                                    <b-button
                                        size="is-large"
                                        type="is-info"
                                        expanded
                                        :disabled="!applyReady(app) || processing"
                                        @click="apply(app)"
                                        v-else
                                    >
                                        <i class="fas fa-arrow-right mr-4"></i>Apply for <strong> {{ app.applications.certification_certs.certifications.unit_title }}</strong>
                                    </b-button>
                                </div>
                            </div>
                        </div>

                        <div v-else-if="getEnrollApp(app).is_rejected">
                            <b-message type="is-danger" class="mt-5">
                                <div class="mb-3 has-text-weight-bold">{{ app.applications.type | capitalize }} Rejected</div>
                                <div v-if="getEnrollApp(app).review_message" class="review-message">{{ getEnrollApp(app).review_message }}</div>
                                <div v-else>This {{ app.applications.type }} was reviewed and not accepted.</div>
                            </b-message>
                        </div>

                        <div v-else-if="!getEnrollApp(app).completed_at">
                            <div class="is-size-6 mt-4 has-text-weight-bold"><i class="fas fa-exclamation-triangle has-text-warning mr-2"></i>Application Incomplete. You are on step {{ getEnrollApp(app).progress }} of {{ app.applications.application_outline.length + ((getEnrollApp(app).quoted_price) ? 2 : 1) }}.</div>
                            <div class="is-size-6 mt-4 mb-2 has-text-grey" v-if="getEnrollApp(app).review_at"><i class="far fa-fw fa-info-circle mr-2 has-text-warning"></i>You have reached a step that requires further review and approval. Please allow up to 48 hours for this step to be completed.</div>
                            <hr/>
                            <b-button :type="(getEnrollApp(app).review_at) ? 'is-light' : 'is-danger'" tag="router-link" :to="{name: 'course-application', params: {applicationId: getEnrollApp(app).id}}"><strong>Continue {{ app.applications.type | capitalize }}</strong><i class="far fa-arrow-right-long ml-3"></i></b-button>
                        </div>

                        <div v-else>
                            <div class="is-size-6 mt-2 has-text-grey"><i class="far fa-fw fa-check-circle mr-2 has-text-success"></i>{{ app.applications.type | capitalize }} completed and approved.</div>
                            <hr/>
                            <b-button type="is-success" tag="router-link" :to="{name: 'course-application', params: {applicationId: getEnrollApp(app).id}}"><strong>View {{ app.applications.type | capitalize }}</strong><i class="far fa-arrow-right-long ml-3"></i></b-button>
                        </div>
                    </section>

                </div>
            </div>
        </ui-card>
    </div>
</template>

<script>
    import {client as http} from "../../../http_client";
    import UiCard from "../../../components/UiCard.vue";
    import async from "async";
    import {mapActions, mapGetters} from "vuex";

    export default {
        components: {UiCard},
        props: ['enrollmentId'],
        data: function() {
            return {
                processing: false,
            };
        },
        computed: {
            ...mapGetters(['enrollment', 'milestoneLock', 'applicationReadiness', 'certificationOn', 'certificationById']),
            totalLogMinutes: function() {
                let total = 0;
                for (let i = 0; i < this.enrollment.study_log.length; i++) {
                    total += this.enrollment.study_log[i].mins;
                }
                return total;
            }
        },
        methods: {
            ...mapActions(['getEnrollmentById']),
            start(app) {
                app.ready.toggle = true;
            },
            apply(app) {
                if (!this.processing) {
                    this.processing = true;
                    http.post('app/enrollments/' + this.enrollmentId + '/applications', {course_application_id: app.id}).then(response => {
                        this.$router.push({name: 'course-application', params: {applicationId: response.data.id}});
                    });
                }
            },
            // The certification row (with title) when this application's
            // certification is switched off; null when on or unknown.
            certificationOffFor: function(course_app) {
                const certs = course_app.applications && course_app.applications.certification_certs;
                const id = certs ? certs.certification_id : null;
                if (!id || this.certificationOn(id)) return null;
                return this.certificationById(id);
            },
            getEnrollApp: function(course_app) {
                for (let i = 0; i < this.enrollment.applications.length; i++) {
                    const app = this.enrollment.applications[i];
                    if (app.course_application_id === course_app.id) {
                        return app;
                    }
                }
                return {};
            },
            scrollTo(refName) {
                let element = this.$refs[refName];
                if (element) {
                    element.scrollIntoView({behavior: 'smooth'});
                }
            },
            availableUnits(app) {
                // What THIS enrollment can be awarded: the enrolled tier caps
                // the course-wide max_units (a 15-hour enrollment on a course
                // whose biggest tier is 50 offers 15, not 50 — matches the
                // server-side cap in apply())
                const max = parseFloat(app.max_units);
                const cap = parseFloat(this.enrollment.configs?.[0]?.max_hours);
                return (cap > 0) ? Math.min(max, cap) : max;
            },
            calcUnits(app) {
                let certification = app.applications.certification_certs.certifications;
                if (certification.type !== 'unit') {
                    return 0;
                }
                let units = this.totalLogMinutes / certification.unit_ratio;
                if (certification.unit_round_to) {
                    units = Math.floor(this.totalLogMinutes / certification.unit_round_to) * certification.unit_round_to;
                }
                return Math.min(this.availableUnits(app), units);
            },
            // Per-enrollment unit-price override (bundles): the student's
            // clock hour fee, scoped to this application's certification.
            // Mirrors apply() server-side, where the override wins.
            overrideFor(app) {
                const certId = app.applications.certification_certs && app.applications.certification_certs.certification_id;
                return certId && (this.enrollment.enrollment_unit_prices || []).find(p => p.certification_id === certId);
            },
            // The per-unit rate the charge is built from (0 when the
            // application is flat-priced or free) — shown in the cost
            // acknowledgment so the math is transparent
            perUnitRate(app) {
                const override = this.overrideFor(app);
                if (override) return parseFloat(override.unit_price);
                if (!parseFloat(app.flat_price) && parseFloat(app.per_unit_price)) return parseFloat(app.per_unit_price);
                return 0;
            },
            // A charge applies to this application — flat, per-unit or a
            // per-enrollment override — regardless of units earned so far.
            // Drives the fee badge and the cost acknowledgment, so per-unit
            // pricing discloses from the start just like a fixed charge.
            hasCharge(app) {
                return this.chargeAmount(app) > 0 || this.perUnitRate(app) > 0;
            },
            unitSingular(app) {
                const title = (app.applications.certification_certs.certifications.unit_title || 'unit').toLowerCase();
                return title.replace(/s\s*$/, '');
            },
            chargeAmount(app) {
                const override = this.overrideFor(app);
                if (override) {
                    return this.calcUnits(app) * parseFloat(override.unit_price);
                }
                let flatPrice = parseFloat(app.flat_price);
                let pricePerUnit = parseFloat(app.per_unit_price);
                if (flatPrice) {
                    return flatPrice;
                } else if (pricePerUnit) {
                    return this.calcUnits(app) * pricePerUnit;
                } else {
                    return 0;
                }
            },
            applyReady(app) {
                if (app.min_minutes_required) {
                    app.ready.minMinutes = this.totalLogMinutes >= app.min_minutes_required;
                } else {
                    app.ready.minMinutes = true;
                }

                // Gate inversion (completion redesign): applying requires finished
                // coursework — not enrollment.completed_at, which the application
                // itself now sets — plus no pending milestone reflection.
                return app.ready.log && app.ready.confirm && this.enrollment.isCourseworkComplete && !this.milestoneLock.locked && app.ready.minMinutes && (!this.hasCharge(app) || app.ready.charge);
            }
        },
        mounted() {
            this.getEnrollmentById(this.enrollmentId);
        }
    };
</script>

<style lang="scss" scoped>

</style>
